// ================================================================================
// helpers.js — Pure helper functions and synthesis logic
// ================================================================================

import React from "react";
import {
    Users, MousePointer, Mail, MessageSquare, Radio, Newspaper, Smartphone,
    Phone, Activity, Calendar, Wind, TrendingUp, MessageSquare as MsgSq,
    Crosshair, Users as UsersIcon
} from "lucide-react";
import { ARCHETYPE_AGE_DEFAULTS, COHORT_INTELLIGENCE } from "./constants";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatChannelName = (name) => {
    if (!name) return 'N/A';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const formatResourceValue = (budget, hours, mode) => {
    if (mode === 'time_only') return `${hours ? hours.toFixed(0) : 0} hrs`;
    if (mode === 'hybrid') return `£${budget ? budget.toLocaleString() : 0} + ${hours ? hours.toFixed(0) : 0} hrs`;
    return `£${budget ? budget.toLocaleString() : 0}`;
};

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

export const getChannelIcon = (channel) => {
    const c = (channel ?? '').toLowerCase();
    if (c.includes('door') || c.includes('canvass')) return <Users size={24} />;
    if (c.includes('digital')) return <MousePointer size={24} />;
    if (c.includes('postal')) return <Mail size={24} />;
    if (c.includes('social')) return <MessageSquare size={24} />;
    if (c.includes('radio')) return <Radio size={24} />;
    if (c.includes('newspaper')) return <Newspaper size={24} />;
    if (c.includes('text')) return <Smartphone size={24} />;
    if (c.includes('phone')) return <Phone size={24} />;
    if (c.includes('event')) return <Calendar size={24} />;
    return <Activity size={24} />;
};

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export const getStdDev = (arr) => {
    if (!arr || arr.length === 0) return 0;
    const mean = arr.reduce((a, b) => a + b) / arr.length;
    return Math.sqrt(arr.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / arr.length);
};

// ---------------------------------------------------------------------------
// Archetype helpers
// ---------------------------------------------------------------------------

export const getArchetypeAgeMap = (archetypeName) => {
    const name = (archetypeName || '').toLowerCase();
    const archetypeMap = {
        "civic pragmatist": "established_suburban_family",
        "graduate professional": "young_professional_urban",
        "working class traditional": "working_class_traditional",
        "established professionals": "established_suburban_family",
        "backbone conservative core": "retirement_community",
        "well-off traditionalist": "established_suburban_family",
        "well-off traditionalist conservative": "retirement_community"
    };
    const mappedName = archetypeMap[name] || "mixed_demographic";
    return ARCHETYPE_AGE_DEFAULTS[mappedName] || ARCHETYPE_AGE_DEFAULTS['mixed_demographic'];
};

export const calculateArchetypeFit = (archetypeMix, channelEffectiveness) => {
    if (!archetypeMix || !channelEffectiveness) return [];
    const effectivenessScores = {};
    Object.entries(channelEffectiveness.age_group_contributions ?? {}).forEach(([age, data]) => {
        effectivenessScores[age] = data.effectiveness_score;
    });
    return archetypeMix.map(archetype => {
        const ageMap = getArchetypeAgeMap(archetype.name);
        let fitScore = 0;
        Object.entries(ageMap).forEach(([age, pct]) => { fitScore += (pct * (effectivenessScores[age] ?? 0)); });
        return { name: archetype.name, percentage: archetype.percentage, fitScore: fitScore * 100 };
    }).sort((a, b) => b.percentage - a.percentage);
};

// ---------------------------------------------------------------------------
// Strategic synthesis
// ---------------------------------------------------------------------------

export const getStrategicDriver = (channels) => {
    if (!channels || channels.length === 0) return { driver: "N/A", rationale: "No channel data to analyze." };
    const fits = channels.map(c => (c.average_demographic_fit ?? 0) * 100);
    const costs = channels.map(c => c.average_shadow_cpv || c.average_cost_per_vote).filter(c => c > 0 && c !== Infinity);

    if (costs.length === 0) return { driver: "Fit-Driven", rationale: "Allocation is based purely on demographic fit." };
    const fitStdDev = getStdDev(fits);
    const costStdDev = getStdDev(costs.map(c => 1 / c));
    if (costStdDev > fitStdDev * 1.5) return { driver: "Resource-Driven", rationale: "Resource efficiency varies significantly. Model prioritized high-ROI (Time or Money) options." };
    if (fitStdDev > costStdDev * 1.5) return { driver: "Fit-Driven", rationale: "Demographic Fit varies significantly. Model prioritized alignment with ward archetypes." };
    return { driver: "Balanced", rationale: "Both resource efficiency and demographic fit are balanced." };
};

export const getStrategicAlignment = (artemisTier, campaignFocus) => {
    if (!artemisTier || !campaignFocus) return { level: 'Medium', label: 'N/A', rationale: 'Missing data for alignment check.' };
    const tier = artemisTier.toUpperCase();
    const focus = campaignFocus.toUpperCase();
    const isGain = tier.includes('GAIN');
    const isDefend = tier.includes('DEFEND') || tier.includes('SAFE');
    const isSwingFocus = focus.includes('SWING') || focus.includes('LATE_DECIDER') || focus.includes('PERSUASION');
    const isBaseFocus = focus.includes('CORE') || focus.includes('TURNOUT') || focus.includes('RETENTION');

    if (isGain && isSwingFocus) return { level: 'High', label: 'Synergistic', rationale: 'Aggressive growth strategy matches Gain opportunity.' };
    if (isDefend && isBaseFocus) return { level: 'High', label: 'Synergistic', rationale: 'Defensive resource allocation matches retention need.' };
    if (isGain && isBaseFocus) return { level: 'Low', label: 'Strategic Friction', rationale: 'Using defensive tactics on a Gain target. Pivot to persuasion recommended.' };
    if (isDefend && isSwingFocus) return { level: 'Medium', label: 'Review Needed', rationale: 'Using persuasion on a Defend seat. Ensure base is secure first.' };
    return { level: 'Medium', label: 'Neutral', rationale: 'No strong conflict or synergy detected. Monitor efficiency.' };
};

export const getUntappedPotential = (tacticalIntelligence) => {
    const sensitivities = tacticalIntelligence?.sensitivities ?? [];
    if (!Array.isArray(sensitivities) || sensitivities.length === 0) {
        return { level: 'N/A', rationale: 'Sensitivity data unavailable.', percent: 0 };
    }
    const bestOpp = sensitivities[0];
    if (!bestOpp) return { level: 'N/A', rationale: 'Sensitivity data unavailable.', percent: 0 };

    const rawElasticity = bestOpp.elasticity;
    const elasticity = (rawElasticity !== undefined && rawElasticity !== null && !isNaN(rawElasticity)) ? Number(rawElasticity) : 0;
    const percent = elasticity > 1.0 ? elasticity : elasticity * 100;
    const channelName = formatChannelName(bestOpp.channel || 'Unknown Channel');

    const thresholdHigh = 50;
    const thresholdMed = 20;

    if (percent > thresholdHigh) return { level: 'High', rationale: `Spending more on ${channelName} yields significant marginal gains.`, percent };
    if (percent > thresholdMed) return { level: 'Medium', rationale: `Moderate gains possible by increasing ${channelName} budget.`, percent };
    return { level: 'Low', rationale: 'Diminishing returns detected. Budget is near saturation point.', percent };
};

export const generateDemographicInsights = (demoAnalysis) => {
    if (!demoAnalysis?.age_group_contributions) return { target: 'N/A', rationale: [] };
    const entries = Object.entries(demoAnalysis.age_group_contributions);
    if (entries.length === 0) return { target: 'N/A', rationale: [] };

    const best = entries.reduce((max, curr) => (curr[1].contribution_percentage > max[1].contribution_percentage ? curr : max));
    const bestAge = best[0];
    const rationale = [];
    rationale.push(`Primary driver is the ${bestAge} demographic (${best[1].contribution_percentage.toFixed(0)}% of impact).`);

    if (bestAge === '18-24') rationale.push("High youth density suggests digital/mobile-first effectiveness.");
    else if (bestAge === '25-34') rationale.push("Emerging professionals (Waithood Cohort). Responsive to housing & career framing.");
    else if (bestAge === '35-44') rationale.push("The 'Rush Hour' cohort. High responsiveness to family support & cost-of-living messaging.");
    else if (bestAge === '45-54') rationale.push("Gen X Consolidation. Values stability, competence, and contract preservation.");
    else if (bestAge === '55-64') rationale.push("Pre-retirement 'Asset Chasm'. Highly sensitive to NHS access and pension protection.");
    else if (bestAge === '64-75') rationale.push("Active Retirees. High turnout group responsive to community and local service messaging.");
    else if (bestAge === '75+') rationale.push("Traditionalist alignment. High responsiveness to postal/direct contact and care issues.");

    return { target: bestAge, rationale };
};

export const getSocialAtmosphere = (socialScore, strategicScore, tier) => {
    const t = (tier || '').toUpperCase();
    const isChallenger = t.includes('GAIN') || t.includes('TARGET') || t.includes('UNWINNABLE');

    if (socialScore === undefined) return { label: "Neutral", desc: "No significant social pressure detected.", color: "text-slate-400", icon: <Wind size={20} /> };

    if (socialScore > 0.015) {
        if (isChallenger) return { label: "Underdog Surge", desc: "Challenger momentum is building. Voters are breaking for the rising alternative.", color: "text-cyan-400", icon: <TrendingUp size={20} /> };
        return { label: "Bandwagon Effect", desc: "Incumbency strength is amplifying support. Voters are rallying around the perceived winner.", color: "text-success-400", icon: <UsersIcon size={20} /> };
    }
    if (socialScore < -0.01) return { label: "Spiral of Silence", desc: "Dominant opposing narratives are suppressing visible support. Expect 'shy' voters.", color: "text-orange-400", icon: <MsgSq size={20} /> };
    if (strategicScore > 0.01) return { label: "Tactical Consolidation", desc: "High-information voters are consolidating behind this party to block a rival.", color: "text-purple-400", icon: <Crosshair size={20} /> };

    return { label: "Stable Atmosphere", desc: "Local dynamics are insulated from national volatility.", color: "text-slate-400", icon: <Wind size={20} /> };
};

export const getInteractionPsychometrics = (metrics) => {
    const irt = metrics?.irt_contact_intensity ?? 0.5;
    const difficulty = irt < 0.3 ? "High Barrier" : irt < 0.6 ? "Moderate Friction" : "Low Resistance";
    let narrative = "";
    if (irt < 0.3) narrative = "Voters are resistant to this channel. Requires high-quality, 'soft' messaging to break through.";
    else if (irt > 0.7) narrative = "Open door environment. Voters are predisposed to engage via this medium.";
    else narrative = "Standard engagement friction. Success depends heavily on message personalization.";
    return { level: difficulty, narrative, score: irt * 100 };
};

export const getPhaseAnalysis = (days, volatility) => {
    const volPct = (volatility * 100).toFixed(0);
    if (days < 7) return { label: "Final Frenzy", desc: `Maximum volatility (${volPct}%). GOTV is the only priority.`, urgency: "Critical" };
    if (days < 30) return { label: "Persuasion Window", desc: `High elasticity. Swing voters are deciding now.`, urgency: "High" };
    return { label: "Foundation Building", desc: "Low volatility. Focus on identification and awareness.", urgency: "Medium" };
};

// ---------------------------------------------------------------------------
// Demographics processing
// ---------------------------------------------------------------------------

export const processDemographics = (contributions) => {
    if (!contributions) return {};

    const grouped = {
        '18-24': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '25-34': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '35-44': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '45-54': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '55-64': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '64-75': { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } },
        '75+':   { male: { pct: 0, profiles: [] }, female: { pct: 0, profiles: [] } }
    };

    Object.entries(contributions).forEach(([key, data]) => {
        const profileKey = key.replace(/^None_/, '');
        const profile = COHORT_INTELLIGENCE.profiles[profileKey];

        if (profile) {
            const ageRange = profile._age_range;
            const bucketKey = ageRange === '75_plus' ? '75+' : ageRange;
            const genderKey = (data.gender || 'Female').toLowerCase();

            if (grouped[bucketKey] && grouped[bucketKey][genderKey]) {
                grouped[bucketKey][genderKey].pct += (data.contribution_percentage ?? 0);
                grouped[bucketKey][genderKey].profiles.push({
                    ...data,
                    profileDetails: profile,
                    context: COHORT_INTELLIGENCE.contexts[ageRange]
                });
            }
        }
    });

    Object.keys(grouped).forEach(age => {
        ['male', 'female'].forEach(g => {
            grouped[age][g].profiles.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
        });
    });

    return grouped;
};
